import React from "react";
import moment from "moment-timezone";
import startCase from "lodash/startCase";
import { useLocation } from "react-router";
import { push } from "connected-react-router";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch } from "react-redux";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";

import { IpdInfoPanel } from "./IpdInfoPanel";
import { IpdStatus } from "../../constants/ipd";
import { t, tl } from "../../components/translate";
import { EmptyView } from "../../components/OList";
import { useGetIpd } from "../../hooks/query/useIpds";
import DischargeForm from "./Discharge/DischargeForm";
import PageControl from "../../components/PageControl";
import useQueryParams from "../../hooks/useQueryParams";
import { IPD, ExtendedIpd, TIpdCase } from "../../interfaces/IpdInterface";
import { IpdAdmissionCreateEdit } from "./IpdAdmission/IpdAdmissionCreateEdit";
import List, { MultipleHeadersInterface } from "../../components/List";

import "./IpdList.scss";

export const formatIpdToExtended = (item: IPD): ExtendedIpd => {
  const last = item.bedAssignments?.length
    ? [...item.bedAssignments].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0]
    : undefined;

  return {
    ...item,
    bedId: last?.bedId,
    bedNumber: last?.bed?.number || "",
    bedCode: last?.bed?.code || "",
    roomCode: last?.bed?.room?.code || "",
    roomType: last?.bed?.room?.type || "",
    wardCode: last?.bed?.room?.ward?.shortCode || "",
    wardName: last?.bed?.room?.ward?.name || "",
    patientFullName: `${item?.ipdRecord?.patientRelatedInformation?.firstName || ""} ${
      item?.ipdRecord?.patientRelatedInformation?.lastName || ""
    }`.trim()
  };
};

export const formatIpdsForList = (ipds: Array<IPD>): Array<ExtendedIpd> => {
  const data = ipds.map(formatIpdToExtended);

  return data.sort((a, b) => {
    const c1: Date = new Date(a.created_at || 0);
    const c2: Date = new Date(b.created_at || 0);
    return c2.getTime() - c1.getTime();
  });
};

export const ipdMultipleHeaders = (url: string): MultipleHeadersInterface => ({
  headers: [
    { key: "beds", title: tl("ipd.dashboard"), goto: "/beds" },
    { key: "ipd", title: tl("ipd.clients"), goto: "/ipd?filter=admitted" },
    { key: "available-rooms", title: tl("ipd.availableRooms"), goto: "/available-rooms" }
  ],
  url
});

export const emergencyMultipleHeaders = (url: string): MultipleHeadersInterface => ({
  headers: [
    { key: "emergency", title: tl("ipd.dashboard"), goto: "/emergency" },
    { key: "emergency-clients", title: tl("ipd.clients"), goto: "/emergency/clients" }
  ],
  url
});

export const pageSize = 25;

export interface IpdListProps {
  isShownInBedsDashboard?: boolean;
  data?: IPD[];
  isLoading?: boolean;
  totalIpdCount?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  defaultIpdCase?: TIpdCase;
}

export default function IpdList({
  isShownInBedsDashboard = false,
  data = [],
  isLoading = false,
  totalIpdCount = 0,
  page = 0,
  onPageChange,
  defaultIpdCase
}: IpdListProps = {}): React.ReactNode {
  const dispatch = useDispatch();
  const location = useLocation();

  // custom hooks
  const query = useQueryParams();

  // variables
  const ipdId = query.get("ipdId");

  // local states
  const [openAdmissionForm, setOpenAdmissionForm] = React.useState(false);
  const [openDischargeForm, setOpenDischargeForm] = React.useState(false);
  const [selectedIpd, setSelectedIpd] = React.useState<ExtendedIpd | null>(null);

  // variables
  const isDischargeSummaryEditable =
    moment
      .tz("Asia/kathmandu")
      .diff(moment.tz(selectedIpd?.dateOfDischarge, "Asia/kathmandu"), "day", true) < 3;

  const ipdStatus = selectedIpd?.status;
  const isDischargedAndNotEditable =
    ipdStatus === IpdStatus.DISCHARGED && !isDischargeSummaryEditable;

  const { data: ipdDetailsRes } = useGetIpd({ id: Number(ipdId) || 0 });

  React.useEffect(() => {
    if (ipdId && ipdDetailsRes?.success && ipdDetailsRes.data) {
      setSelectedIpd(formatIpdToExtended(ipdDetailsRes.data));
    } else if (!ipdId) {
      setSelectedIpd(null);
    }
  }, [ipdId, ipdDetailsRes?.data, ipdDetailsRes?.success]);

  return (
    <>
      <Box
        sx={{
          height: "100%"
        }}
      >
        <List<ExtendedIpd>
          automation="Ipdclients"
          testmationLabel="createIpd"
          hideCreateButton
          rowHeight={50}
          defaultSortOrder={-1}
          defaultSortColumn="name"
          withoutSearch
          multipleHeaders={undefined}
          headerStyles={{ px: 0, py: 0 }}
          isLoading={isLoading}
          data={
            isShownInBedsDashboard
              ? formatIpdsForList(data)
                  .filter((ipd) => ipd.status === IpdStatus.ADMITTED)
                  .slice(0, 15)
              : formatIpdsForList(data)
          }
          isResponsive
          columns={[
            {
              key: "Ipd Number",
              label: "Admission No.",
              formatter: (row: ExtendedIpd) => (
                <Typography component="div" variant="body2">
                  {row.ipdNumber}
                </Typography>
              )
            },
            {
              key: "client name",
              label: `${t("ipd.clientName")}`,
              sortable: true,
              sortFunction: (a: ExtendedIpd, b: ExtendedIpd) =>
                `${a?.patientFullName}` > `${b?.patientFullName}` ? 1 : -1,
              formatter: (row: ExtendedIpd) => (
                <Typography component="div" variant="body2">
                  <Box>{row?.patientFullName.toUpperCase()}</Box>
                </Typography>
              )
            },
            {
              key: "Status",
              label: `${t("ipd.status")}`,
              hideInNarrowView: true,
              formatter: (row: ExtendedIpd) => (
                <Typography
                  component="div"
                  style={{
                    color: `${row.status === "discharged" ? "red" : "green"}`
                  }}
                >
                  {startCase(row.status)}
                </Typography>
              )
            },
            {
              key: "admissionCase",
              label: `${t("ipd.admissionCase")}`,
              formatter: (row: ExtendedIpd) => (
                <Typography component="div" variant="body2">
                  {row.admissionCase || "-"}
                </Typography>
              )
            },
            {
              key: "currentCase",
              label: `${t("ipd.currentCase")}`,
              formatter: (row: ExtendedIpd) => (
                <Typography component="div" variant="body2">
                  {row.currentCase || row.ipdRecord?.case || "-"}
                </Typography>
              )
            },
            {
              key: "Bed",
              label: `${t("ipd.bedCode")}`,
              formatter: (row: ExtendedIpd) => (
                <Typography component="div" variant="body2">
                  <Box>{row.bedCode || "-"}</Box>
                </Typography>
              )
            },
            {
              key: "Room Code",
              label: `${t("ipd.roomCode")}`,
              formatter: (row: ExtendedIpd) => (
                <Typography component="div" variant="body2">
                  <Box>{row.roomCode || "-"}</Box>
                </Typography>
              )
            },
            {
              key: "Ward Code",
              label: `${t("ipd.wardCode")}`,
              formatter: (row: ExtendedIpd) => (
                <Typography component="div" variant="body2">
                  <Box>{row.wardCode || "-"}</Box>
                </Typography>
              )
            },
            {
              hideInNarrowView: true,
              key: "Doctor In Charge",
              label: `${t("ipd.docInCharge.")}`,
              formatter: (row: ExtendedIpd) => (
                <Typography component="div" variant="body2">
                  <Box>{`${row.doctorInCharge?.firstName} ${row.doctorInCharge?.lastName}`}</Box>
                </Typography>
              )
            }
          ].filter((c) => {
            if (!isShownInBedsDashboard) return c;
            return ["client name", "Status", "Bed", "Ward Code"].includes(c.key);
          })}
          activeRow={selectedIpd && selectedIpd.id}
          onRowClick={async (ipd) => {
            setSelectedIpd(ipd);
            setOpenAdmissionForm(false);
            setOpenDischargeForm(false);
          }}
        >
          <EmptyView>
            <Typography component="div" variant="body2">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "350px",
                  width: "100%"
                }}
              >
                <Typography component="div">
                  <Box textAlign="center" marginTop="16px">
                    No IPD found.
                  </Box>
                </Typography>
              </Box>
            </Typography>
          </EmptyView>
        </List>

        {!isShownInBedsDashboard && (
          <Box className="navigatePage">
            <PageControl
              page={page}
              pageSize={pageSize}
              onSetPage={(v) => {
                if (onPageChange) {
                  onPageChange(v);
                }
              }}
              maximumDataCount={totalIpdCount || 0}
            />
          </Box>
        )}
      </Box>

      {selectedIpd && (
        <IpdInfoPanel
          ipd={selectedIpd}
          editButton={
            ipdStatus === IpdStatus.ADMITTED ? (
              <IconButton
                data-testmation="editButton"
                disabled={isDischargedAndNotEditable}
                onClick={() => {
                  setOpenAdmissionForm(true);
                }}
              >
                <EditIcon />
              </IconButton>
            ) : (
              <Tooltip
                title="Discharge summary can only be edited within 24 hours discharge."
                arrow
              >
                <IconButton
                  data-testmation="editButton"
                  disabled={isDischargedAndNotEditable}
                  onClick={() => {
                    setOpenDischargeForm(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )
          }
          onClose={() => {
            setSelectedIpd(null);

            if (ipdId) {
              query.delete("ipdId");
              dispatch(
                push({
                  pathname: location.pathname,
                  search: query.toString()
                })
              );
            }
          }}
          onDischargeFormOpen={() => setOpenDischargeForm(true)}
          onIpdChange={(updatedIpd: IPD) => {
            // Here we need to update the ipd details
            const extendedIpd = formatIpdToExtended(updatedIpd);
            setSelectedIpd(extendedIpd);
          }}
        />
      )}

      {openAdmissionForm && (
        <IpdAdmissionCreateEdit
          ipd={selectedIpd}
          defaultIpdCase={defaultIpdCase}
          onClose={() => {
            setOpenAdmissionForm(false);
          }}
        />
      )}

      {openDischargeForm && selectedIpd && (
        <DischargeForm
          ipdData={selectedIpd}
          onClose={() => setOpenDischargeForm(false)}
          onDischargeSuccess={() => {
            setOpenDischargeForm(false);
            query.append("status", IpdStatus.DISCHARGED);
            dispatch(
              push({
                pathname: location.pathname,
                search: query.toString()
              })
            );
          }}
        />
      )}
    </>
  );
}
